import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Badge, Button, Link, Stack, TextField, Typography } from '@platform/design-system';
import { useSession } from "../auth/session.js";
import { AuthFlowError } from "../auth/types.js";
import { describeError } from "../http/errors.js";
import { MOCK_EMAIL_CODE } from "../mock/world.js";
import { newPasswordForm, PASSWORD_HINT, totpCodeSchema, } from "./password-rules.js";
/**
 * The steps the pre-auth screens share: a new password pair, a 6-digit code, and confirming a
 * self sign-up with the emailed code. Each is one form that answers through its caller.
 */
const codeForm = z.object({ code: totpCodeSchema });
export function NewPasswordStep({ busy, onSubmit, submitLabel = 'Set password and continue', extra, }) {
    const form = useForm({
        resolver: zodResolver(newPasswordForm),
        defaultValues: { password: '', confirm: '' },
    });
    const submit = form.handleSubmit(onSubmit);
    return (_jsx("form", { onSubmit: (e) => void submit(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [extra, _jsx(TextField, { label: "New password", type: "password", autoComplete: "new-password", ...form.register('password'), error: form.formState.errors.password?.message, hint: PASSWORD_HINT }), _jsx(TextField, { label: "Confirm new password", type: "password", autoComplete: "new-password", ...form.register('confirm'), error: form.formState.errors.confirm?.message }), _jsx(Button, { type: "submit", variant: "contained", size: "large", busy: busy, busyLabel: "Saving\u2026", children: submitLabel })] }) }));
}
export function CodeStep({ busy, label, submitLabel, onSubmit, }) {
    const form = useForm({ resolver: zodResolver(codeForm), defaultValues: { code: '' } });
    const submit = form.handleSubmit(onSubmit);
    return (_jsx("form", { onSubmit: (e) => void submit(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [_jsx(TextField, { label: label, autoComplete: "one-time-code", inputMode: "numeric", ...form.register('code'), error: form.formState.errors.code?.message }), _jsx(Button, { type: "submit", variant: "contained", size: "large", busy: busy, busyLabel: "Checking\u2026", children: submitLabel })] }) }));
}
/**
 * Confirm a self sign-up (ADR *personal-tenants-sign-up-in-the-cell-pool*): the code the pool
 * emailed, a way to have it sent again, and nothing else. `onConfirmed` runs once the identity
 * is confirmed — also when it already was (a second tab, a repeated click), because the person's
 * next step is the same either way. Mock mode shows the code it accepts.
 */
export function ConfirmEmailStep({ email, submitLabel = 'Confirm email', busy = false, onConfirmed, }) {
    const session = useSession();
    const [checking, setChecking] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const confirm = async (code) => {
        setChecking(true);
        setError(null);
        setNotice(null);
        try {
            await session.confirmSignUp({ email, code });
            onConfirmed();
        }
        catch (e) {
            if (e instanceof AuthFlowError && e.code === 'already-confirmed')
                onConfirmed();
            else
                setError(describeError(e, 'We could not check that code. Try again.'));
        }
        finally {
            setChecking(false);
        }
    };
    const resend = async () => {
        setResending(true);
        setError(null);
        setNotice(null);
        try {
            const { destination } = await session.resendSignUpCode(email);
            setNotice(`A new code is on its way to ${destination ?? email}.`);
        }
        catch (e) {
            if (e instanceof AuthFlowError && e.code === 'already-confirmed')
                onConfirmed();
            else
                setError(describeError(e, 'We could not send a new code. Try again.'));
        }
        finally {
            setResending(false);
        }
    };
    return (_jsxs(Stack, { spacing: 2, children: [session.mode === 'mock' && (_jsx(Badge, { tone: "warning", label: `Mock session — the code is ${MOCK_EMAIL_CODE}` })), error && _jsx(Alert, { severity: "error", children: error }), notice && (_jsx(Alert, { severity: "success", role: "status", children: notice })), _jsx(CodeStep, { busy: checking || busy, label: "Code from the email", submitLabel: submitLabel, onSubmit: (v) => void confirm(v.code) }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["No email? Check your spam folder, or", ' ', _jsx(Link, { component: "button", type: "button", variant: "body2", onClick: () => void resend(), disabled: resending, "aria-busy": resending, children: resending ? 'sending a new code…' : 'send a new code' }), "."] })] }));
}
//# sourceMappingURL=auth-steps.js.map